'use client';

import { usePathname, useSearchParams } from 'next/navigation';
import { useEffect, useMemo, useRef } from 'react';
import { analyticsPlugin } from './plugins/analytics';
import { broadstreetRefreshPlugin } from './plugins/broadstreet';
import type { NavigationContext, NavigationPlugin } from './types';

type Props = {
  additionalPlugins?: NavigationPlugin[];
  context?: Record<string, unknown>;
};

export function NavigationManager({
  additionalPlugins = [],
  context = {},
}: Props) {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const prevPathRef = useRef<string | null>(null);

  const contextKey = JSON.stringify(context);
  const stableContext = useMemo(() => context, [contextKey]);

  const allPlugins = useMemo(
    () => [analyticsPlugin, broadstreetRefreshPlugin, ...additionalPlugins],
    [additionalPlugins]
  );

  useEffect(() => {
    const searchString = searchParams.toString();
    const currentPath = pathname + searchString;

    const previousPath = prevPathRef.current;
    if (previousPath === currentPath) return;
    const isFirstLoad = previousPath === null;
    prevPathRef.current = currentPath;
    if (isFirstLoad) return;

    const navCtx: NavigationContext = {
      ...stableContext,
      currentPath,
      previousPath,
      pathname,
      searchParams: searchString,
    };

    for (const plugin of allPlugins) {
      console.debug(`[NavigationManager] Running plugin "${plugin.name}"`);
      try {
        const result = plugin.handler(navCtx);
        if (result instanceof Promise) {
          result.catch((err) =>
            console.error(
              `[NavigationManager] Plugin "${plugin.name}" threw:`,
              err
            )
          );
        }
      } catch (err) {
        console.error(
          `[NavigationManager] Plugin "${plugin.name}" threw:`,
          err
        );
      }
    }
  }, [pathname, searchParams, stableContext, allPlugins]);

  return null;
}
